import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ThreadQuill.css';
import useMessageDraft, { emptyDraft } from './useMessageDraft';

const modules = { toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] };
const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

function DraftEditor({ threadType, conversationId, identity, isAdmin, onSend, onSent, placeholder }) {
  const draft = useMessageDraft(threadType, conversationId, identity);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState('');
  const input = useRef(null);
  const active = useRef(true);
  const sending = useRef(false);
  useEffect(() => () => { active.current = false; }, []);
  const blocked = busy || ['loading', 'load-error', 'conflict'].includes(draft.status);
  const tooLong = draft.text.length > 5000;
  const handleSend = async () => {
    if (sending.current || blocked || tooLong || (emptyDraft(draft.text) && !file)) return;
    sending.current = true;
    setBusy(true);
    setSendError('');
    try {
      if (!await draft.save()) return;
      if (!active.current) return;
      const result = await onSend({ text: draft.text, file, draftRevision: emptyDraft(draft.text) ? undefined : draft.revision() });
      if (!result) { setSendError('Nie udało się wysłać wiadomości. Wersja robocza została zachowana.'); return; }
      setFile(null);
      await draft.sent();
      if (onSent) onSent();
    } catch { setSendError('Nie udało się wysłać wiadomości. Wersja robocza została zachowana.'); }
    finally { sending.current = false; if (active.current) setBusy(false); }
  };
  const statusText = {
    loading: 'Wczytywanie wersji roboczej…',
    saved: emptyDraft(draft.text) ? 'Wersja robocza jest pusta.' : 'Wersja robocza zapisana.',
    unsaved: 'Oczekiwanie na zapis…', saving: 'Zapisywanie wersji roboczej…',
    error: 'Nie udało się zapisać na serwerze. Treść pozostaje w edytorze. Ponów zapis przed wysłaniem.',
    'load-error': 'Nie udało się wczytać wersji roboczej. Ponów wczytywanie.',
    conflict: 'Wersja robocza została zmieniona w innym oknie. Skopiuj swoje zmiany przed wczytaniem zapisanej wersji.',
  }[draft.status];

  return (
    <form onSubmit={e => e.preventDefault()} className="email-reply-box">
      <div className="draft-status" role="status" aria-live="polite">{statusText}</div>
      {isAdmin && <p className="draft-privacy">Wersję roboczą widzą tylko administratorzy mający dostęp do tego wątku. Uczestnicy zobaczą wiadomość dopiero po kliknięciu „Wyślij”.</p>}
      {draft.status === 'conflict' && <button type="button" className="draft-action" onClick={draft.reload}>Wczytaj zapisaną wersję</button>}
      {draft.status === 'load-error' && <button type="button" className="draft-action" onClick={draft.retryLoad}>Ponów wczytywanie</button>}
      <div className="thread-quill" style={{ marginBottom: '8px' }}>
        <ReactQuill value={draft.text} onChange={(value, _delta, source) => { if (source === 'user' && !busy) draft.change(value); }} readOnly={blocked} modules={modules} formats={formats} theme="snow" placeholder={placeholder || 'Napisz wiadomość…'} />
      </div>
      {tooLong && <p role="alert" className="draft-error">Treść z formatowaniem może mieć maksymalnie 5000 znaków.</p>}
      {file && <div className="draft-attachment"><span>{file.name}</span><button type="button" disabled={busy} onClick={() => setFile(null)}>Usuń załącznik</button><small>Załącznik nie jest zapisywany w wersji roboczej. Dodaj go przed wysłaniem.</small></div>}
      <div className="draft-actions">
        <button type="button" className="draft-action" disabled={blocked} onClick={() => input.current.click()} aria-label="Dodaj załącznik"><i className="fas fa-paperclip" aria-hidden="true" /> Załącznik</button>
        <input ref={input} type="file" style={{ display: 'none' }} onChange={e => { const selected = e.target.files[0]; if (selected && selected.size > 10 * 1024 * 1024) setSendError('Załącznik może mieć maksymalnie 10 MB.'); else { setFile(selected || null); setSendError(''); } e.target.value = ''; }} />
        <button type="button" className="draft-action" disabled={blocked || tooLong} onClick={() => draft.save()}>Zapisz wersję roboczą</button>
        <button type="button" className="draft-action" disabled={blocked || emptyDraft(draft.text)} onClick={async () => { setBusy(true); try { if (await draft.discard()) setFile(null); } finally { setBusy(false); } }}>Usuń wersję roboczą</button>
        <button type="button" className="draft-send" disabled={blocked || tooLong || (emptyDraft(draft.text) && !file)} onClick={handleSend}>{busy ? 'Proszę czekać…' : 'Wyślij'}</button>
      </div>
      {sendError && <p role="alert" className="draft-error">{sendError}</p>}
    </form>
  );
}

export default function MessageComposer(props) {
  const { userEmail, userRole } = useSelector(state => state.authorization);
  if (!userEmail || !userRole) return null;
  const identity = `${userRole}:${userEmail.toLowerCase()}`;
  return <DraftEditor {...props} identity={identity} isAdmin={userRole === 'admin'} key={`${identity}:${props.threadType}:${props.conversationId}`} />;
}
